import {
  Component,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VideoPlayerOpenOptions, VideoPlayerService } from '../../../core/services/video-player.service';
import { StorageService } from '../../../core/services/storage.service';
import { NotificationService } from '../../../core/services/notification.service';
import { resolveVideoMimeType } from '../../utils/video-mime.util';

@Component({
  selector: 'app-video-player-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './video-player-modal.component.html',
  styleUrls: ['./video-player-modal.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class VideoPlayerModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly videoPlayerService = inject(VideoPlayerService);
  private readonly storageService = inject(StorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  open = false;
  title = '';
  videoUrl: string | null = null;
  videoMimeType = 'video/mp4';
  loading = false;

  ngOnInit(): void {
    this.videoPlayerService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        if (!request) {
          this.reset();
          return;
        }
        this.loadRequest(request);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOverlayClick(): void {
    this.close();
  }

  close(): void {
    this.videoPlayerService.close();
  }

  private loadRequest(request: VideoPlayerOpenOptions): void {
    this.open = true;
    this.title = request.title;
    this.videoUrl = null;
    this.videoMimeType = resolveVideoMimeType(request.contentType, request.mimeType);
    this.loading = true;

    if (request.videoUrl) {
      this.videoUrl = request.videoUrl;
      this.loading = false;
      return;
    }

    if (!request.s3Key) {
      this.handleLoadError();
      return;
    }

    this.storageService
      .generateDownloadUrl(request.s3Key, { inline: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.downloadUrl) {
            this.videoUrl = response.data.downloadUrl;
          } else {
            this.handleLoadError();
          }
          this.loading = false;
        },
        error: () => {
          this.handleLoadError();
          this.loading = false;
        }
      });
  }

  private handleLoadError(): void {
    this.reset();
    this.notificationService.error(
      this.translateService.instant('dashboard.common.error'),
      this.translateService.instant('dashboard.common.videoPlayer.loadError')
    );
  }

  private reset(): void {
    this.open = false;
    this.title = '';
    this.videoUrl = null;
    this.loading = false;
  }
}
